import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useNavigate } from "react-router-dom";
import { CrownIcon as Crown, HeartIcon as Heart, MagnifyingGlassIcon as MagnifyingGlass, TruckIcon as Truck, CheckIcon as Check, ScissorsIcon as Scissors, StorefrontIcon as Storefront } from "@phosphor-icons/react";

/** Friendly micro-onboarding step that fires once on first dashboard visit
 *  if the user hasn't picked a persona yet. Six large click-targets covering
 *  every audience the platform serves:
 *    breeder            → Kennels & breeders
 *    owner              → Solo owner with one or two dogs
 *    buyer              → Aspiring buyer
 *    transporter        → Licensed transporter
 *    service_provider   → Trainer / groomer / walker / sitter
 *    storefront_seller  → Independent storefront (food, merch, services)
 *  Selected persona drives subtle copy/CTA tailoring across the app
 *  (Dashboard headline; Help/Tour copy). The user can also dismiss the
 *  dialog and pick later from /settings.
 *
 *  IMPORTANT: this dialog auto-skips when the user has already derived a
 *  non-buyer primary persona from data signals (approved provider row,
 *  active store, transporter onboarded, etc.) so users who landed here
 *  via /become-a-provider, /me/store, or /transport/apply don't get
 *  asked to re-pick their role.
 */
const OPTIONS = [
  {
    key: "breeder",
    icon: Crown,
    title: "I run a kennel",
    blurb: "Multiple dogs, recording pedigrees and litters, planning matings, transferring ownership to buyers.",
  },
  {
    key: "owner",
    icon: Heart,
    title: "I'm an owner who loves my dog(s)",
    blurb: "One or two cherished dogs. Mostly here to track records, share photos, and follow other dog lovers.",
  },
  {
    key: "buyer",
    icon: MagnifyingGlass,
    title: "I'm hoping to find a dog",
    blurb: "Browsing kennels, watching litters, learning enough about pedigrees to make an informed pick.",
  },
  {
    key: "transporter",
    icon: Truck,
    title: "I'm a licensed transporter",
    blurb: "USDA-licensed ground transport. Bidding on routes, building a repeat-client list, getting paid via Stripe Connect.",
  },
  {
    key: "international_transporter",
    icon: Truck,
    title: "I'm an international courier",
    blurb: "Foreign-based, globally mobile — I fly dogs across borders for clients. Get verified buyer leads on a Lead Pass subscription.",
  },
  {
    key: "service_provider",
    icon: Scissors,
    title: "I'm a trainer, groomer, walker, or sitter",
    blurb: "Service business pros offering bookings. We list you in /find-pros and route payments via Stripe Connect.",
  },
  {
    key: "storefront_seller",
    icon: Storefront,
    title: "I sell food, merch, or services",
    blurb: "Independent storefront on /store/{slug} — Stripe Tax + Connect, local delivery + nationwide shipping ready.",
  },
];

export default function PersonaPickerDialog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  // Use a ref instead of state so React 18 strict-mode's deliberate effect
  // double-invocation can't accidentally cancel the in-flight persona check
  // before its `.then` resolves. The dialog still only fires once per mount.
  const checkedRef = useRef(false);

  useEffect(() => {
    if (!user || checkedRef.current) return;
    checkedRef.current = true;
    // Fire BOTH calls in parallel. If `/me/persona` returns a value the
    // user already self-declared, skip. If `/me/nav-context` returns a
    // primary other than 'buyer' (data-derived signal — approved
    // provider row, active store, transporter onboarded), also skip
    // so providers/storefront-sellers who joined via their dedicated
    // onboarding flow never see this picker.
    Promise.all([
      api.get("/me/persona").catch(() => ({ data: {} })),
      api.get("/me/nav-context").catch(() => ({ data: {} })),
      api.get("/auth/me").catch(() => ({ data: {} })),
    ]).then(([persona, nav, me]) => {
      const hasDeclaredPersona = !!persona?.data?.persona;
      const navPrimary = nav?.data?.primary;
      const hasDerivedPersona = navPrimary && navPrimary !== "buyer";
      // LocationGate is pending for accounts without a country (Apple /
      // Google social signups). A modal Radix dialog would make the gate
      // inert — Apple review bug 2.1(a) — so never open under it.
      const locationGatePending = !me?.data?.country;
      if (hasDeclaredPersona || hasDerivedPersona || locationGatePending) return;
      setOpen(true);
      window.dispatchEvent(new CustomEvent("persona-dialog-open"));
    });
  }, [user]);

  const onSubmit = async (personaKey) => {
    const chosen = personaKey || selected;
    if (!chosen) return;
    setBusy(true);
    try {
      await api.post("/me/persona", { persona: chosen });
      // Best-effort: emit a custom event so any open page can hot-update its
      // copy without a full reload. Currently the Dashboard listens.
      window.dispatchEvent(new CustomEvent("persona-changed", { detail: { persona: chosen } }));
      setOpen(false);
      // Personas with a dedicated onboarding flow go straight there —
      // a stranded pro on a generic dashboard is lost supply.
      const dest = {
        service_provider: "/become-a-provider",
        storefront_seller: "/me/store",
        transporter: "/transporter",
      }[chosen];
      if (dest) navigate(dest);
    } catch {
      /* failure surfaces via toast elsewhere; keep dialog open for retry */
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next && !selected) {
          // Dialog closed without a selection (X button / Escape / outside
          // click). Treat the same as the "Maybe later" link so downstream
          // listeners (e.g. product tour) can still proceed.
          window.dispatchEvent(new CustomEvent("persona-skipped"));
        }
      }}
    >
      <DialogContent className="max-w-2xl" data-testid="persona-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-3xl tracking-tight">
            Welcome — what brings you here?
          </DialogTitle>
          <DialogDescription>
            One quick pick so we can tailor the experience. You can change
            it anytime from Settings.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 mt-2">
          {OPTIONS.map(({ key, icon: Icon, title, blurb }) => {
            const isSelected = selected === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setSelected(key);
                  onSubmit(key); // one tap commits — no scroll-to-confirm
                }}
                data-testid={`persona-option-${key}`}
                className={`text-left flex items-start gap-4 p-4 md:p-5 rounded-xl border transition-all ${
                  isSelected
                    ? "border-[#0E0E0E] bg-[#FBF7EC] shadow-[0_0_0_3px_rgba(201,162,39,0.25)]"
                    : "border-[#E5E5E5] hover:border-[#0E0E0E]/40 hover:bg-[#FAFAFA]"
                }`}
              >
                <div
                  className={`p-2.5 rounded-lg flex-shrink-0 ${
                    isSelected ? "bg-[#C9A227]/15 border border-[#C9A227]/40" : "bg-[#FAFAFA] border border-[#E5E5E5]"
                  }`}
                >
                  <Icon size={20} weight="duotone" className={isSelected ? "text-[#C9A227]" : "text-[#6B6B6B]"} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-spectral text-lg tracking-tight">{title}</h3>
                  <p className="text-sm text-[#6B6B6B] mt-1 leading-relaxed">{blurb}</p>
                </div>
                {isSelected && (
                  <Check size={18} weight="bold" className="text-ink flex-shrink-0 mt-1" />
                )}
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-3 mt-4">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              // Tell the rest of the app the user chose to defer; the
              // product-tour hook listens for this so it can still kick off
              // its first-run walkthrough even when no persona was picked.
              window.dispatchEvent(new CustomEvent("persona-skipped"));
            }}
            className="text-xs text-[#6B6B6B] hover:text-ink underline-offset-2 hover:underline"
            data-testid="persona-skip"
          >
            Maybe later
          </button>
          <p className="text-[11px] text-[#9A9A9A]">{busy ? "Saving…" : "Tap one to continue"}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
